package com.dotpx.ambient

import kotlin.math.PI
import kotlin.math.exp
import kotlin.math.max

/**
 * Seeded, allocation-free oscillator imperfection. Slow smooth random lanes
 * emulate tiny pitch/gain drift; the filtered noise output adds faint grain
 * without changing any scene/event random sequence.
 */
internal class AnalogImperfectionState(
    private val sampleRate: Double,
    seed: ULong,
) {
    private val controlRandom = SplitMix64(0uL)
    private val noiseRandom = SplitMix64(0uL)

    private var pitchFrom = 0.0
    private var pitchTo = 0.0
    private var pitchFrame = 0
    private var pitchFrames = 2
    private var secondaryFrom = 0.0
    private var secondaryTo = 0.0
    private var secondaryFrame = 0
    private var secondaryFrames = 2
    private var gainFrom = 0.0
    private var gainTo = 0.0
    private var gainFrame = 0
    private var gainFrames = 2

    private var noiseFastLeft = 0f
    private var noiseFastRight = 0f
    private var noiseSlowLeft = 0f
    private var noiseSlowRight = 0f
    private val noiseFastCoefficient = (1.0 - exp(-2.0 * PI * 1_800.0 / sampleRate)).toFloat()
    private val noiseSlowCoefficient = (1.0 - exp(-2.0 * PI * 35.0 / sampleRate)).toFloat()

    var pitchMultiplier = 1.0
        private set
    var secondaryPitchMultiplier = 1.0
        private set
    var amplitudeMultiplier = 1f
        private set
    var noiseLeft = 0f
        private set
    var noiseRight = 0f
        private set

    init {
        require(sampleRate.isFinite() && sampleRate > 0.0) { "sampleRate must be finite and positive" }
        reset(seed)
    }

    fun reset(seed: ULong) {
        controlRandom.reset(seed xor 0xD6E8_FEB8_6659_FD93uL)
        noiseRandom.reset(seed xor 0xA5A3_564E_27F8_864DuL)

        pitchFrom = 0.0
        pitchTo = controlRandom.nextUnitDouble() * 2.0 - 1.0
        pitchFrame = 0
        pitchFrames = segmentFrames(0.70, 2.20)
        secondaryFrom = 0.0
        secondaryTo = controlRandom.nextUnitDouble() * 2.0 - 1.0
        secondaryFrame = 0
        secondaryFrames = segmentFrames(0.45, 1.60)
        gainFrom = 0.0
        gainTo = controlRandom.nextUnitDouble() * 2.0 - 1.0
        gainFrame = 0
        gainFrames = segmentFrames(1.10, 3.80)

        noiseFastLeft = 0f
        noiseFastRight = 0f
        noiseSlowLeft = 0f
        noiseSlowRight = 0f
        pitchMultiplier = 1.0
        secondaryPitchMultiplier = 1.0
        amplitudeMultiplier = 1f
        noiseLeft = 0f
        noiseRight = 0f
    }

    fun nextFrame() {
        val pitchAmount = smoothAmount(pitchFrame, pitchFrames)
        val secondaryAmount = smoothAmount(secondaryFrame, secondaryFrames)
        val gainAmount = smoothAmount(gainFrame, gainFrames)
        pitchMultiplier = 1.0 + lerp(pitchFrom, pitchTo, pitchAmount) * PITCH_DEPTH
        secondaryPitchMultiplier =
            1.0 + lerp(secondaryFrom, secondaryTo, secondaryAmount) * SECONDARY_PITCH_DEPTH
        amplitudeMultiplier =
            (1.0 + lerp(gainFrom, gainTo, gainAmount) * AMPLITUDE_DEPTH).toFloat()

        pitchFrame += 1
        if (pitchFrame >= pitchFrames) {
            pitchFrom = pitchTo
            pitchTo = controlRandom.nextUnitDouble() * 2.0 - 1.0
            pitchFrame = 0
            pitchFrames = segmentFrames(0.70, 2.20)
        }
        secondaryFrame += 1
        if (secondaryFrame >= secondaryFrames) {
            secondaryFrom = secondaryTo
            secondaryTo = controlRandom.nextUnitDouble() * 2.0 - 1.0
            secondaryFrame = 0
            secondaryFrames = segmentFrames(0.45, 1.60)
        }
        gainFrame += 1
        if (gainFrame >= gainFrames) {
            gainFrom = gainTo
            gainTo = controlRandom.nextUnitDouble() * 2.0 - 1.0
            gainFrame = 0
            gainFrames = segmentFrames(1.10, 3.80)
        }

        val rawLeft = noiseRandom.nextBipolarFloat()
        val rawRight = noiseRandom.nextBipolarFloat()
        noiseFastLeft += noiseFastCoefficient * (rawLeft - noiseFastLeft)
        noiseFastRight += noiseFastCoefficient * (rawRight - noiseFastRight)
        noiseSlowLeft += noiseSlowCoefficient * (noiseFastLeft - noiseSlowLeft)
        noiseSlowRight += noiseSlowCoefficient * (noiseFastRight - noiseSlowRight)
        noiseLeft = noiseFastLeft - noiseSlowLeft
        noiseRight = noiseFastRight - noiseSlowRight
    }

    private fun segmentFrames(lowSeconds: Double, highSeconds: Double): Int = max(
        ((lowSeconds + (highSeconds - lowSeconds) * controlRandom.nextUnitDouble()) * sampleRate).toInt(),
        2,
    )

    private fun smoothAmount(frame: Int, frames: Int): Double {
        val linear = frame.toDouble() / max(frames - 1, 1).toDouble()
        return linear * linear * (3.0 - 2.0 * linear)
    }

    private fun lerp(start: Double, end: Double, amount: Double): Double =
        start + (end - start) * amount

    internal companion object {
        const val PITCH_DEPTH = 0.002_8
        const val SECONDARY_PITCH_DEPTH = 0.001_8
        const val AMPLITUDE_DEPTH = 0.032
        const val NOISE_MIX = 0.012f
    }
}
